/**
 * App Preview Modal
 *
 * Full-screen preview for generated Losi App drafts before saving.
 *
 * Primary responsibilities:
 * - Let users try tabs, setup inputs, and AI actions locally
 * - Apply safe preview edits to the draft before app creation
 *
 * Dependencies:
 * - Shared Dialog, Button, Badge, Input, and Textarea primitives
 * - App definition schema
 */

'use client'

import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'
import {
  ArrowLeft,
  Check,
  Circle,
  FileText,
  LayoutGrid,
  Play,
  Upload,
  X
} from 'lucide-react'
import { LosiAssistantDefault } from 'marshmallow-icons'

import { AppIcon } from '@/components/apps/app-icon'
import {
  APP_SOURCE_FILE_ACCEPT,
  readAppSourceFileEvidence
} from '@/components/apps/app-source-files'
import { editAppDraft, runAppPreviewAction } from '@/components/apps/apps-api-client'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  getModelByKey,
  type AppAction,
  type AppDefinition,
  type AppPage,
  type AppSampleRecord
} from '@/lib/apps/definition'
import { cn } from '@/lib/utils'
import type { AppPreviewDraftState } from '@/types/losi-apps'

interface AppPreviewModalProps {
  backLabel?: string
  definition: AppDefinition
  explanation?: string
  isCreating?: boolean
  onCreate: () => void
  onDefinitionChange: (definition: AppDefinition) => void
  onOpenChange: (open: boolean) => void
  onPreviewStateChange?: (state: AppPreviewDraftState) => void
  open: boolean
  workspaceId?: string | null
}

interface PreviewRun {
  actionKey: string
  findings: string[]
  status?: 'failed' | 'needs_input' | 'succeeded'
  summary: string
  title: string
}

interface PreviewRecord {
  data: Record<string, unknown>
  id: string
  modelKey: string
  status?: string
  title: string
}

type SetupValues = Record<string, string>

// --------------

export function AppPreviewModal({
  backLabel = 'Back to prompt',
  definition,
  explanation,
  isCreating = false,
  onCreate,
  onDefinitionChange,
  onOpenChange,
  onPreviewStateChange,
  open,
  workspaceId
}: AppPreviewModalProps) {
  const [activeTab, setActiveTab] = useState(definition.navigation[0]?.key ?? '')
  const [setupValues, setSetupValues] = useState<SetupValues>(() =>
    buildInitialSetupValues(definition)
  )
  const [previewRun, setPreviewRun] = useState<PreviewRun | null>(null)
  const [sourceImageDataUrls, setSourceImageDataUrls] = useState<string[]>([])
  const [sourceText, setSourceText] = useState('')
  const [sourceMessage, setSourceMessage] = useState<string | null>(null)
  const [isReadingSource, setIsReadingSource] = useState(false)
  const [askPrompt, setAskPrompt] = useState('')
  const [askMessage, setAskMessage] = useState<string | null>(null)
  const [isAskingLosi, setIsAskingLosi] = useState(false)
  const [runningActionKey, setRunningActionKey] = useState<string | null>(null)
  const sourceReadIdRef = useRef(0)

  useEffect(() => {
    setActiveTab((current) =>
      definition.navigation.some((page) => page.key === current)
        ? current
        : (definition.navigation[0]?.key ?? '')
    )
    setSetupValues((current) => mergeSetupValues(definition, current))
  }, [definition])

  const activePage = useMemo(
    () =>
      definition.navigation.find((page) => page.key === activeTab) ??
      definition.navigation[0] ??
      null,
    [activeTab, definition]
  )
  const previewRecords = useMemo(
    () => buildPreviewRecords(definition, setupValues, previewRun),
    [definition, previewRun, setupValues]
  )
  const visibleActions = useMemo(() => getVisibleActions(definition), [definition])
  const acceptsSourceInput = useMemo(
    () => visibleActions.some(usesPreviewSourceInput),
    [visibleActions]
  )

  useEffect(() => {
    onPreviewStateChange?.({
      previewRun,
      sourceImageDataUrls,
      sourceText,
      setupValues
    })
  }, [onPreviewStateChange, previewRun, setupValues, sourceImageDataUrls, sourceText])

  const updateSetupValue = (key: string, value: string) => {
    setPreviewRun(null)
    setSetupValues((current) => ({ ...current, [key]: value }))
  }

  const appendSourceText = (value: string) => {
    setPreviewRun(null)
    setSourceText((current) => (current.trim() ? `${current.trim()}\n\n${value}` : value))
  }

  const addSourceImage = (imageDataUrl: string) => {
    setPreviewRun(null)
    setSourceImageDataUrls((current) => [...current.slice(-1), imageDataUrl])
  }

  const updateSourceText = (value: string) => {
    setPreviewRun(null)
    setSourceText(value)
  }

  const startSourceRead = () => {
    sourceReadIdRef.current += 1
    setPreviewRun(null)
    setIsReadingSource(true)
    return sourceReadIdRef.current
  }

  const isCurrentSourceRead = (sourceReadId: number) => {
    return sourceReadIdRef.current === sourceReadId
  }

  const endSourceRead = (sourceReadId: number) => {
    if (isCurrentSourceRead(sourceReadId)) {
      setIsReadingSource(false)
    }
  }

  const runPreviewAction = async (action: AppAction, input: Record<string, unknown> = {}) => {
    if (isReadingSource) {
      setAskMessage('Losi is still reading that source file. Try the action again in a moment.')
      return false
    }

    if (!workspaceId) {
      setAskMessage('Choose a workspace before Losi tests this preview.')
      return false
    }

    setRunningActionKey(action.key)
    setAskMessage(null)

    try {
      const response = await runAppPreviewAction({
        actionKey: action.key,
        definition,
        input: sanitizePreviewActionInput(input),
        setupValues,
        sourceImageDataUrls,
        sourceText: sourceText.trim() || undefined,
        workspaceId
      })

      setPreviewRun(response.previewRun)
      if (response.sourceImageDataUrls?.length) {
        setSourceImageDataUrls((current) =>
          mergeSourceImageDataUrls(current, response.sourceImageDataUrls ?? [])
        )
      }

      const targetTab = action.outputModel
        ? definition.navigation.find((page) => page.modelKey === action.outputModel)?.key
        : null

      if (targetTab) {
        setActiveTab(targetTab)
      }
      return true
    } catch {
      setAskMessage('Losi could not test this preview right now. Your draft is still here.')
      return false
    } finally {
      setRunningActionKey(null)
    }
  }

  const handleAskLosi = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const request = askPrompt.trim()
    if (!request) return

    if (!workspaceId) {
      setAskMessage('Choose a workspace before Losi changes this preview.')
      return
    }

    setIsAskingLosi(true)
    setAskMessage(null)

    try {
      const response = await editAppDraft({
        definition,
        instruction: request,
        workspaceId
      })
      if (response.intent === 'run_request' && response.actionKey) {
        const action = definition.actions.find((item) => item.key === response.actionKey)

        if (action) {
          const didRun = await runPreviewAction(action, response.actionInput ?? {})
          if (didRun) {
            setAskMessage(response.summary)
          }
          setAskPrompt('')
          return
        }
      }

      const addedPage = response.definition.navigation.find(
        (page) => !definition.navigation.some((current) => current.key === page.key)
      )

      onDefinitionChange(response.definition)
      setPreviewRun(null)
      setActiveTab(addedPage?.key ?? activeTab)
      setAskMessage(response.summary)
      setAskPrompt('')
    } catch {
      setAskMessage('Losi could not update this preview right now. Your draft is still here.')
    } finally {
      setIsAskingLosi(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border/70 flex max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-[1180px] flex-col gap-0 overflow-hidden p-0 shadow-2xl sm:rounded-xl">
        <DialogTitle className="sr-only">{definition.app.name} preview</DialogTitle>
        <DialogDescription className="sr-only">
          Try the generated app before creating it.
        </DialogDescription>

        <header className="border-border/70 bg-background/95 flex flex-col gap-4 border-b p-4 backdrop-blur md:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="bg-muted flex size-12 shrink-0 items-center justify-center rounded-lg border">
                <AppIcon className="size-6" icon={definition.app.icon} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-2xl font-semibold tracking-tight">
                    {definition.app.name}
                  </h2>
                  <ReadinessBadge status={definition.readiness.status} />
                </div>
                <p className="text-muted-foreground mt-1 max-w-3xl text-sm leading-6">
                  {definition.app.description}
                </p>
                {explanation ? (
                  <p className="text-muted-foreground mt-2 text-xs">{explanation}</p>
                ) : null}
              </div>
            </div>

            <div className="flex w-full shrink-0 flex-wrap gap-2 md:w-auto md:justify-end">
              <Button
                className="w-full sm:w-auto"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                <ArrowLeft className="size-4" />
                {backLabel}
              </Button>
              <Button
                className="w-full sm:w-auto"
                disabled={isReadingSource || isCreating}
                loading={isCreating}
                onClick={onCreate}
              >
                Create App
              </Button>
              <Button
                aria-label="Close preview"
                size="icon"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 overflow-y-auto overscroll-contain xl:grid-cols-[250px_minmax(0,1fr)_290px] xl:overflow-hidden">
          <aside className="border-border/70 bg-muted/20 min-h-0 border-b p-4 xl:overflow-y-auto xl:border-b-0 xl:border-r">
            <PreviewTabs
              activeTab={activeTab}
              pages={definition.navigation}
              onTabChange={setActiveTab}
            />
            <SetupChecklist
              definition={definition}
              setupValues={setupValues}
              onSetupChange={updateSetupValue}
            />
          </aside>

          <section className="min-h-0 min-w-0 p-4 md:p-5 xl:overflow-y-auto">
            {activePage ? (
              <ActivePagePreview
                definition={definition}
                page={activePage}
                previewRecords={previewRecords}
                previewRun={previewRun}
              />
            ) : null}
          </section>

          <aside className="border-border/70 bg-muted/20 min-h-0 border-t p-4 xl:overflow-y-auto xl:border-l xl:border-t-0">
            <FirstBestAction
              disabled={isReadingSource}
              definition={definition}
              runningActionKey={runningActionKey}
              onRunPreviewAction={runPreviewAction}
            />
            {acceptsSourceInput ? (
              <PreviewSourceInput
                fileMessage={sourceMessage}
                isReadingSource={isReadingSource}
                onAddSourceImage={addSourceImage}
                onAppendSourceText={appendSourceText}
                isCurrentSourceRead={isCurrentSourceRead}
                sourceText={sourceText}
                workspaceId={workspaceId}
                onFileReadEnd={endSourceRead}
                onFileReadStart={startSourceRead}
                onFileMessage={setSourceMessage}
                onSourceTextChange={updateSourceText}
              />
            ) : null}
            <ActionCards
              actions={visibleActions}
              disabled={isReadingSource}
              runningActionKey={runningActionKey}
              onRunPreviewAction={runPreviewAction}
            />
            <AskLosiPreviewForm
              askMessage={askMessage}
              askPrompt={askPrompt}
              isSubmitting={isAskingLosi}
              onAskPromptChange={setAskPrompt}
              onSubmit={handleAskLosi}
            />
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --------------

function PreviewTabs({
  activeTab,
  onTabChange,
  pages
}: {
  activeTab: string
  onTabChange: (key: string) => void
  pages: AppPage[]
}) {
  return (
    <div>
      <div className="text-muted-foreground flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]">
        <LayoutGrid className="size-4" />
        Pages
      </div>
      <div className="mt-3 space-y-1">
        {pages.map((page) => (
          <button
            key={page.key}
            type="button"
            className={cn(
              'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors',
              activeTab === page.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
            )}
            onClick={() => onTabChange(page.key)}
          >
            <span className="truncate">{page.label}</span>
            <span className="bg-primary/50 size-1.5 rounded-full" />
          </button>
        ))}
      </div>
    </div>
  )
}

// --------------

function SetupChecklist({
  definition,
  onSetupChange,
  setupValues
}: {
  definition: AppDefinition
  onSetupChange: (key: string, value: string) => void
  setupValues: SetupValues
}) {
  const items = [
    ...definition.readiness.requiredInputs.map((item) => ({ ...item, optional: false })),
    ...definition.readiness.optionalImprovements.map((item) => ({ ...item, optional: true }))
  ]

  if (items.length === 0) {
    return (
      <section className="bg-background mt-5 rounded-lg border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Check className="size-4 text-emerald-600" />
          Ready to try
        </div>
      </section>
    )
  }

  return (
    <section className="bg-background mt-5 rounded-lg border p-4">
      <p className="text-sm font-semibold">Setup checklist</p>
      <div className="mt-3 space-y-3">
        {items.map((item) => {
          const value = setupValues[item.key] ?? ''
          const complete = Boolean(value.trim())

          return (
            <div key={item.key} className="space-y-2">
              <div className="flex items-start gap-2 text-sm">
                {complete ? (
                  <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                ) : (
                  <Circle
                    className={cn(
                      'mt-0.5 size-4 shrink-0',
                      item.optional ? 'text-muted-foreground' : 'text-amber-600'
                    )}
                  />
                )}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{item.label}</span>
                    {complete ? (
                      <StatusBadge tone="success">Added</StatusBadge>
                    ) : item.optional ? (
                      <Badge variant="outline">Optional</Badge>
                    ) : (
                      <StatusBadge tone="warning">Needed</StatusBadge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs leading-5">{item.reason}</p>
                </div>
              </div>
              <Input
                value={value}
                placeholder={getSetupPlaceholder(item.type, item.label)}
                type={item.type === 'url' ? 'url' : 'text'}
                onChange={(event) => onSetupChange(item.key, event.target.value)}
              />
            </div>
          )
        })}
      </div>
    </section>
  )
}

// --------------

function ActivePagePreview({
  definition,
  page,
  previewRecords,
  previewRun
}: {
  definition: AppDefinition
  page: AppPage
  previewRecords: PreviewRecord[]
  previewRun: PreviewRun | null
}) {
  const pageRecords = page.modelKey
    ? previewRecords.filter((record) => record.modelKey === page.modelKey)
    : previewRecords.slice(0, 4)

  return (
    <div className="space-y-4">
      <div>
        <Badge variant="secondary">Live preview</Badge>
        <h3 className="mt-3 text-xl font-semibold tracking-tight">{page.label}</h3>
        <p className="text-muted-foreground mt-1 text-sm leading-6">
          This is how the app will feel once you create it.
        </p>
      </div>

      <PreviewMetrics definition={definition} previewRecords={previewRecords} />

      {previewRun ? <PreviewRunOutput previewRun={previewRun} /> : null}

      <section className="bg-card rounded-lg border p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">
              {page.modelKey ? 'Preview content' : 'Page content'}
            </h4>
            <p className="text-muted-foreground mt-1 text-xs">
              Real examples are included so the app is not empty on day one.
            </p>
          </div>
          <FileText className="text-muted-foreground size-4" />
        </div>
        <div className="mt-4 grid min-w-0 gap-3 xl:grid-cols-2">
          {pageRecords.length > 0
            ? pageRecords.map((record) => (
                <PreviewRecordCard key={record.id} definition={definition} record={record} />
              ))
            : page.blocks.slice(0, 4).map((block) => (
                <div key={block.key} className="bg-background rounded-md border p-3">
                  <p className="text-sm font-medium">{block.title ?? page.label}</p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Ready for records, outputs, and actions.
                  </p>
                </div>
              ))}
        </div>
      </section>
    </div>
  )
}

// --------------

function PreviewMetrics({
  definition,
  previewRecords
}: {
  definition: AppDefinition
  previewRecords: PreviewRecord[]
}) {
  const metrics = [
    { label: 'Tabs', value: String(definition.navigation.length) },
    { label: 'AI actions', value: String(definition.actions.length) },
    { label: 'Examples', value: String(previewRecords.length) }
  ]

  return (
    <div className="grid min-w-0 gap-3 sm:grid-cols-3">
      {metrics.map((metric) => (
        <div key={metric.label} className="bg-card rounded-lg border p-4 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold uppercase tracking-[0.14em]">
            {metric.label}
          </p>
          <p className="mt-2 text-2xl font-semibold">{metric.value}</p>
        </div>
      ))}
    </div>
  )
}

// --------------

function PreviewRunOutput({ previewRun }: { previewRun: PreviewRun }) {
  const tone = previewRun.status === 'failed'
    ? 'border-destructive/30 bg-destructive/5 text-destructive'
    : previewRun.status === 'needs_input'
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : 'border-emerald-200 bg-emerald-50 text-emerald-950'

  return (
    <section className={cn('rounded-lg border p-4', tone)}>
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
        <LosiAssistantDefault
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0"
          color="currentColor"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{previewRun.title}</p>
          <p className="mt-1 text-sm leading-6 opacity-85">{previewRun.summary}</p>
          <div className="mt-3 grid min-w-0 gap-2 lg:grid-cols-3">
            {previewRun.findings.map((finding) => (
              <div
                key={finding}
                className="min-w-0 break-words rounded-md bg-white/70 p-3 text-xs leading-5"
              >
                {finding}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs opacity-75">
            This is draft preview output. Nothing has been published, sent, or changed outside
            this preview.
          </p>
        </div>
      </div>
    </section>
  )
}

// --------------

function PreviewRecordCard({
  definition,
  record
}: {
  definition: AppDefinition
  record: PreviewRecord
}) {
  const model = getModelByKey(definition, record.modelKey)
  const fields = model?.fields.slice(0, 3) ?? []

  return (
    <article className="bg-background min-w-0 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold">{record.title}</p>
          <p className="text-muted-foreground mt-1 text-xs">{model?.label ?? 'Record'}</p>
        </div>
        {record.status ? <Badge variant="outline">{formatStatus(record.status)}</Badge> : null}
      </div>
      {fields.length > 0 ? (
        <dl className="mt-3 space-y-2">
          {fields.map((field) => (
            <div key={field.key} className="text-xs">
              <dt className="text-muted-foreground">{field.label}</dt>
              <dd className="mt-0.5 break-words font-medium">
                {formatPreviewValue(record.data[field.key])}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </article>
  )
}

// --------------

function FirstBestAction({
  disabled,
  definition,
  runningActionKey,
  onRunPreviewAction
}: {
  disabled: boolean
  definition: AppDefinition
  runningActionKey: string | null
  onRunPreviewAction: (action: AppAction) => Promise<boolean>
}) {
  const action = definition.actions.find(
    (item) => item.key === definition.readiness.firstBestAction?.key
  )

  if (!action) return null

  return (
    <section className="bg-background rounded-lg border p-4">
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-[0.14em]">
        First best action
      </p>
      <p className="mt-2 text-sm font-semibold">{getPreviewActionLabel(action)}</p>
      <p className="text-muted-foreground mt-1 text-xs leading-5">
        Try the main action here before creating the app.
      </p>
      <Button
        className="mt-3 w-full"
        disabled={disabled}
        loading={runningActionKey === action.key}
        onClick={() => void onRunPreviewAction(action)}
      >
        <Play className="size-4" />
        {getPreviewActionLabel(action)}
      </Button>
    </section>
  )
}

// --------------

function ActionCards({
  actions,
  disabled,
  runningActionKey,
  onRunPreviewAction
}: {
  actions: AppAction[]
  disabled: boolean
  runningActionKey: string | null
  onRunPreviewAction: (action: AppAction) => Promise<boolean>
}) {
  return (
    <section className="mt-4">
      <p className="text-sm font-semibold">Actions</p>
      <div className="mt-3 space-y-2">
        {actions.map((action) => (
          <article key={action.key} className="bg-background min-w-0 rounded-lg border p-3">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium">{getPreviewActionLabel(action)}</p>
                <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-5">
                  {action.description ?? 'Try this with the preview content.'}
                </p>
              </div>
              {action.approvalRequired ? <Badge variant="outline">Approval</Badge> : null}
            </div>
            <Button
              className="mt-3 w-full"
              disabled={disabled}
              size="sm"
              variant={action.primary ? 'default' : 'outline'}
              loading={runningActionKey === action.key}
              onClick={() => void onRunPreviewAction(action)}
            >
              <Play className="size-4" />
              Try action
            </Button>
          </article>
        ))}
      </div>
    </section>
  )
}

// --------------

function PreviewSourceInput({
  fileMessage,
  isReadingSource,
  isCurrentSourceRead,
  onAddSourceImage,
  onAppendSourceText,
  onFileReadEnd,
  onFileReadStart,
  onFileMessage,
  onSourceTextChange,
  sourceText,
  workspaceId
}: {
  fileMessage: string | null
  isReadingSource: boolean
  isCurrentSourceRead: (sourceReadId: number) => boolean
  onAddSourceImage: (imageDataUrl: string) => void
  onAppendSourceText: (value: string) => void
  onFileReadEnd: (sourceReadId: number) => void
  onFileReadStart: () => number
  onFileMessage: (message: string | null) => void
  onSourceTextChange: (value: string) => void
  sourceText: string
  workspaceId?: string | null
}) {
  const inputId = useId()
  const [fileName, setFileName] = useState('')

  return (
    <section className="bg-background mt-4 rounded-lg border p-4">
      <p className="text-sm font-semibold">Input for testing</p>
      <p className="text-muted-foreground mt-1 text-xs leading-5">
        Add page text, screenshot notes, a PDF, or a source file before trying an action.
      </p>
      <Textarea
        className="mt-3 min-h-[120px] resize-y"
        value={sourceText}
        placeholder="Paste page text or describe what Losi should review."
        onChange={(event) => onSourceTextChange(event.target.value)}
      />
      <input
        id={inputId}
        accept={APP_SOURCE_FILE_ACCEPT}
        className="sr-only"
        type="file"
        onChange={(event) =>
          void (async () => {
            const file = event.currentTarget.files?.[0] ?? null
            if (!file) return

            const sourceReadId = onFileReadStart()
            setFileName(file.name)
            onFileMessage(null)

            try {
              const evidence = await readAppSourceFileEvidence(file, { workspaceId })
              if (!isCurrentSourceRead(sourceReadId)) return
              if (!evidence) return

              onAppendSourceText(evidence.text)
              if (evidence.imageDataUrl) {
                onAddSourceImage(evidence.imageDataUrl)
              }
              onFileMessage(
                evidence.imageDataUrl
                  ? 'Added uploaded screenshot for visual review.'
                  : 'Added uploaded source content.'
              )
            } catch (error) {
              if (!isCurrentSourceRead(sourceReadId)) return

              onFileMessage(
                error instanceof Error ? error.message : 'Losi could not read that file.'
              )
            } finally {
              onFileReadEnd(sourceReadId)
            }
          })()
        }
      />
      <label
        htmlFor={inputId}
        className="mt-3 flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
      >
        <Upload className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          {isReadingSource ? 'Reading source file...' : fileName || 'Choose source file'}
        </span>
      </label>
      {fileMessage ? (
        <p className="bg-muted text-muted-foreground mt-2 rounded-md px-3 py-2 text-xs">
          {fileMessage}
        </p>
      ) : null}
      {isReadingSource ? (
        <p className="text-muted-foreground mt-2 text-xs">Reading source file...</p>
      ) : null}
    </section>
  )
}

// --------------

function AskLosiPreviewForm({
  askMessage,
  askPrompt,
  isSubmitting,
  onAskPromptChange,
  onSubmit
}: {
  askMessage: string | null
  askPrompt: string
  isSubmitting: boolean
  onAskPromptChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) {
  return (
    <form className="bg-background mt-4 rounded-lg border p-4" onSubmit={onSubmit}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <LosiAssistantDefault aria-hidden="true" className="size-4" color="currentColor" />
        Ask Losi
      </div>
      <Textarea
        className="mt-3 min-h-[84px]"
        value={askPrompt}
        placeholder="Ask Losi to change or test this app..."
        disabled={isSubmitting}
        onChange={(event) => onAskPromptChange(event.target.value)}
      />
      {askMessage ? (
        <p className="bg-muted text-muted-foreground mt-2 rounded-md px-3 py-2 text-xs">
          {askMessage}
        </p>
      ) : null}
      <Button
        className="mt-3 w-full"
        disabled={isSubmitting}
        loading={isSubmitting}
        size="sm"
        type="submit"
        variant="outline"
      >
        Apply to preview
      </Button>
    </form>
  )
}

// --------------

function ReadinessBadge({ status }: { status: AppDefinition['readiness']['status'] }) {
  if (status === 'ready') {
    return (
      <StatusBadge tone="success" dot>
        Ready
      </StatusBadge>
    )
  }

  if (status === 'needs_connection') {
    return (
      <StatusBadge tone="info" dot>
        Needs connection
      </StatusBadge>
    )
  }

  if (status === 'needs_input') {
    return (
      <StatusBadge tone="warning" dot>
        Needs input
      </StatusBadge>
    )
  }

  return (
    <StatusBadge tone="neutral" dot>
      Ready to try
    </StatusBadge>
  )
}

// --------------

function buildInitialSetupValues(definition: AppDefinition): SetupValues {
  const values: SetupValues = {}

  for (const item of [
    ...definition.readiness.requiredInputs,
    ...definition.readiness.optionalImprovements
  ]) {
    values[item.key] = ''
  }

  return values
}

// --------------

function mergeSetupValues(definition: AppDefinition, current: SetupValues): SetupValues {
  return {
    ...buildInitialSetupValues(definition),
    ...current
  }
}

// --------------

function buildPreviewRecords(
  definition: AppDefinition,
  setupValues: SetupValues,
  previewRun: PreviewRun | null
): PreviewRecord[] {
  const records = definition.sampleRecords.map((record, index) => ({
    data: hydrateSampleData(definition, record, setupValues),
    id: `${record.modelKey}-${index}`,
    modelKey: record.modelKey,
    status: record.status,
    title: record.title
  }))

  const previewAction = definition.actions.find((action) => action.key === previewRun?.actionKey)

  if (
    !previewRun ||
    previewRun.status === 'failed' ||
    previewRun.status === 'needs_input' ||
    !previewAction?.outputModel ||
    previewRun.findings.length === 0
  ) {
    return records
  }

  const outputModel = previewAction.outputModel

  previewRun.findings.slice(0, 8).forEach((finding, index) => {
    records.push({
      data: {
        evidence: finding,
        priority: index === 0 ? 'high' : 'medium',
        recommendation: finding,
        summary: previewRun.summary
      },
      id: `${outputModel}-preview-output-${index}`,
      modelKey: outputModel,
      status: 'preview',
      title: finding.slice(0, 100) || `${previewRun.title} ${index + 1}`
    })
  })

  return records
}

// --------------

function hydrateSampleData(
  definition: AppDefinition,
  record: AppSampleRecord,
  setupValues: SetupValues
) {
  const setupData = [
    ...definition.readiness.requiredInputs,
    ...definition.readiness.optionalImprovements
  ]
    .filter((item) => item.modelKey === record.modelKey)
    .reduce<Record<string, string>>((data, item) => {
      const value = setupValues[item.key]?.trim()

      if (value) {
        data[item.fieldKey ?? item.key] = value
      }

      return data
    }, {})

  return {
    ...record.data,
    ...setupData
  }
}

// --------------

function getVisibleActions(definition: AppDefinition) {
  const primaryActions = definition.actions.filter((action) => action.primary)
  const secondaryActions = definition.actions.filter((action) => !action.primary)

  return [...primaryActions, ...secondaryActions]
}

// --------------

function getPreviewActionLabel(action: AppAction) {
  return action.label
}

// --------------

function usesPreviewSourceInput(action: AppAction) {
  const sourceCapabilities = new Set([
    'browser.openAndInspect',
    'file.read',
    'losiComputer.browserSession',
    'screenshot.capture',
    'web.fetch'
  ])

  return (
    action.executionStrategy?.preferredCapability === 'manual.input' ||
    getPreviewActionCapabilities(action).some((capability) =>
      sourceCapabilities.has(capability)
    )
  )
}

// --------------

function getPreviewActionCapabilities(action: AppAction) {
  return [
    action.executionStrategy?.preferredCapability,
    ...(action.executionStrategy?.fallbacks ?? [])
  ].flatMap((capability) => (capability ? [capability] : []))
}

// --------------

function sanitizePreviewActionInput(input: Record<string, unknown>) {
  const safeInput = { ...input }
  delete safeInput.content
  delete safeInput.copy
  delete safeInput.pageText
  delete safeInput.page_text
  delete safeInput.screenshotDescription
  delete safeInput.screenshot_description
  delete safeInput.screenshotNotes
  delete safeInput.screenshot_notes
  delete safeInput.sourceImageDataUrl
  delete safeInput.source_image_data_url
  delete safeInput.sourceImageDataUrls
  delete safeInput.source_image_data_urls
  delete safeInput.screenshotDataUrl
  delete safeInput.screenshot_data_url
  delete safeInput.imageDataUrl
  delete safeInput.image_data_url
  delete safeInput.sourceOrigin
  delete safeInput.sourceText
  delete safeInput.source_text
  return safeInput
}

// --------------

function mergeSourceImageDataUrls(current: string[], incoming: string[]) {
  return [...new Set([...current, ...incoming])].slice(-2)
}

// --------------

function getSetupPlaceholder(type: string, label: string) {
  if (type === 'url') return 'https://example.com'
  if (type === 'email') return 'name@example.com'
  return `Add ${label.toLowerCase()}`
}

// --------------

function formatPreviewValue(value: unknown) {
  if (typeof value === 'string') return value || 'Not set'
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.join(', ')
  return value == null ? 'Not set' : 'Included'
}

// --------------

function formatStatus(value: string) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
